/**
 * All calculations used by analysis orders and analysis results, in one place.
 *
 * Analysis order: no numeric calculations. Co % is entered per sample and only passed on to the result.
 *
 * Analysis result (Hc / MM / Density section and the results summary):
 *
 *   Input            Where it comes from
 *   σs  (µTm³/kg)    Entered manually per sample in the Hc / MM / Density table (measured on an external instrument).
 *   Co  (wt%)        1. XRF analysis "Co" value for the sample, if entered.
 *                    2. Otherwise the sample's Co % from the analysis order.
 *                    3. Otherwise missing.
 *
 *   Output           Formula                        Calculated when
 *   CoM              σs / 2.01                      σs is a number. Co is not used.
 *   MM               63.37 − 32.29 × (σs / Co)      σs is a number and a Co value (> 0) is available.
 *
 * Missing or non-numeric inputs give an empty result; they are never treated as zero.
 * A Co value of 0 cannot be divided by, so it is skipped and the next Co source is used.
 * Results are rounded to 2 decimals and shown with a comma separator.
 */
import { formatDecimal, parseDecimal, toDisplayDecimal } from "../../../utils/decimal";

/** CoM = σs / COM_SIGMA_DIVISOR */
export const COM_SIGMA_DIVISOR = 2.01;
/** MM = MM_INTERCEPT − MM_SLOPE × (σs / Co) */
export const MM_INTERCEPT = 63.37;
export const MM_SLOPE = 32.29;

export type CoContentSource = "xrf" | "order";

export interface ResolvedCoContent {
  value: number;
  /** The Co as it was entered (comma separator), so the summary shows what the user typed. */
  displayValue: string;
  source: CoContentSource;
}

/** CoM from σs only. Returns null when σs is missing or not a number. */
export function calculateCoM(sigmaS: number | null): number | null {
  return sigmaS === null ? null : sigmaS / COM_SIGMA_DIVISOR;
}

/** MM from σs and Co content. Returns null when either is missing, or when Co is not positive. */
export function calculateMM(sigmaS: number | null, coContent: number | null): number | null {
  if (sigmaS === null || coContent === null || coContent <= 0) return null;
  return MM_INTERCEPT - MM_SLOPE * (sigmaS / coContent);
}

/** Picks the Co content for MM: XRF first, then the order's sample Co %. Null when neither has a usable (> 0) value. */
export function resolveCoContent(xrfCo: string | number | null | undefined, orderCo: string | number | null | undefined): ResolvedCoContent | null {
  const candidates: [CoContentSource, string | number | null | undefined][] = [["xrf", xrfCo], ["order", orderCo]];
  for (const [source, raw] of candidates) {
    const value = parseDecimal(raw);
    if (value !== null && value > 0) {
      return { value, displayValue: toDisplayDecimal(String(raw).trim()), source };
    }
  }
  return null;
}

/** CoM and MM as display strings ("" when they cannot be calculated). */
export function calculateMagneticResults(sigmaValue: string | undefined, co: ResolvedCoContent | null) {
  const sigmaS = parseDecimal(sigmaValue);
  const coM = calculateCoM(sigmaS);
  const mm = calculateMM(sigmaS, co?.value ?? null);
  return {
    coM: coM === null ? "" : formatDecimal(coM),
    mm: mm === null ? "" : formatDecimal(mm),
  };
}
